import { useCallback, useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { Link } from "react-router-dom";
import { Warning, MagnifyingGlass, ArrowSquareOut, CaretDown, CaretRight, ListChecks } from "@phosphor-icons/react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";

const fmtMoney = (n) => `$ ${Number(n || 0).toLocaleString("es-AR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const fmtKg = (n) => `${Number(n || 0).toLocaleString("es-AR", { minimumFractionDigits: 0, maximumFractionDigits: 3 })}`;

export default function VentasListaIncorrectaCard({ fechaDesde, fechaHasta }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [q, setQ] = useState("");
  const [tipoFiltro, setTipoFiltro] = useState("todos"); // todos | lista_ajena | fuera_de_lista
  const [expanded, setExpanded] = useState({}); // {factura_id: bool}
  const [visibleCount, setVisibleCount] = useState(10);

  const cargar = useCallback(async () => {
    if (!fechaDesde || !fechaHasta) return;
    setLoading(true);
    try {
      const { data } = await api.get("/dashboard/ventas-lista-incorrecta", {
        params: { fecha_desde: fechaDesde, fecha_hasta: fechaHasta, tolerancia_pct: 0.5 },
      });
      setData(data);
    } catch (_) { setData(null); }
    finally { setLoading(false); }
  }, [fechaDesde, fechaHasta]);
  useEffect(() => { cargar(); }, [cargar]);

  const ventas = data?.ventas || [];
  const filtered = useMemo(() => {
    const s = q.trim().toLowerCase();
    return ventas.filter((v) => {
      // Filtro por tipo
      if (tipoFiltro !== "todos") {
        const hit = (v.items_con_error || []).some((it) => it.tipo_flag === tipoFiltro);
        if (!hit) return false;
      }
      if (!s) return true;
      return (
        (v.cliente_nombre || "").toLowerCase().includes(s) ||
        (v.numero || "").toLowerCase().includes(s) ||
        (v.lista_cliente_nombre || "").toLowerCase().includes(s) ||
        (v.items_con_error || []).some((it) => (it.descripcion || "").toLowerCase().includes(s))
      );
    });
  }, [ventas, q, tipoFiltro]);

  const cantAjena = ventas.reduce((s, v) => s + (v.items_con_error || []).filter(i => i.tipo_flag === "lista_ajena").length, 0);
  const cantFuera = ventas.reduce((s, v) => s + (v.items_con_error || []).filter(i => i.tipo_flag === "fuera_de_lista").length, 0);

  // Al cambiar filtros o dataset resetear el contador visible a 10.
  useEffect(() => { setVisibleCount(10); }, [q, tipoFiltro, ventas.length]);

  const visibleRows = useMemo(
    () => (visibleCount >= filtered.length ? filtered : filtered.slice(0, visibleCount)),
    [filtered, visibleCount]
  );

  const toggle = (fid) => setExpanded((s) => ({ ...s, [fid]: !s[fid] }));

  return (
    <div className="border border-[#E5E5E5] bg-white rounded-sm" data-testid="dash-ventas-lista-incorrecta">
      <div className="px-5 py-3 border-b border-[#E5E5E5] flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h2 className="font-heading font-semibold text-[15px] flex items-center gap-2">
            <ListChecks size={16} weight="bold" className="text-[#6B1F24]" />
            Ventas con Lista de Precios Incorrecta
          </h2>
          <div className="text-[11px] text-[#666] font-mono mt-0.5">
            Detecta ítems facturados con un precio que no corresponde a la lista asignada al cliente · tolerancia ±0,5 %
          </div>
        </div>
        <div className="flex items-center gap-2">
          <div className="relative">
            <MagnifyingGlass size={12} className="absolute left-2 top-1/2 -translate-y-1/2 text-neutral-400" />
            <Input
              placeholder="Buscar cliente, factura o producto…" value={q} onChange={(e) => setQ(e.target.value)}
              className="h-8 pl-7 w-64 text-[12px]"
              data-testid="lista-incorrecta-search"
            />
          </div>
          <select
            value={tipoFiltro}
            onChange={(e) => setTipoFiltro(e.target.value)}
            className="h-8 border border-neutral-300 rounded-sm text-[12px] px-2 bg-white"
            data-testid="lista-incorrecta-tipo"
          >
            <option value="todos">Todos ({cantAjena + cantFuera})</option>
            <option value="lista_ajena">Lista ajena ({cantAjena})</option>
            <option value="fuera_de_lista">Fuera de lista ({cantFuera})</option>
          </select>
        </div>
      </div>

      {loading && (
        <div className="p-8 text-center text-neutral-500 text-sm">Cargando…</div>
      )}
      {!loading && ventas.length === 0 && (
        <div className="p-8 text-center text-neutral-400 text-sm">
          Sin ventas con lista incorrecta en el período. Escaneadas: {data?.total_ventas_escaneadas || 0}.
        </div>
      )}
      {!loading && ventas.length > 0 && (
        <>
          <div className="px-5 py-2 border-b border-amber-200 bg-amber-50 flex items-center gap-3 flex-wrap text-[12px] text-amber-900">
            <Warning size={16} weight="fill" className="text-amber-700" />
            <span>
              <span className="font-bold">{data.total_ventas_con_error}</span> venta{data.total_ventas_con_error === 1 ? "" : "s"} con{" "}
              <span className="font-bold">{data.total_items_con_error}</span> ítem{data.total_items_con_error === 1 ? "" : "s"} desfasado{data.total_items_con_error === 1 ? "" : "s"} · Escaneadas {data.total_ventas_escaneadas}
            </span>
            <span className="ml-auto text-amber-800 font-mono text-[11px]">
              Rango: {data.desde} → {data.hasta}
            </span>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-[12px]">
              <thead className="bg-neutral-50 text-[10px] uppercase font-mono text-neutral-600 sticky top-0">
                <tr>
                  <th className="px-3 py-2 text-left w-6"></th>
                  <th className="px-3 py-2 text-left w-24">Fecha</th>
                  <th className="px-3 py-2 text-left w-32">Comprobante</th>
                  <th className="px-3 py-2 text-left">Cliente · Lista</th>
                  <th className="px-3 py-2 text-center w-24">Ítems c/error</th>
                  <th className="px-3 py-2 text-right w-28">Total factura</th>
                  <th className="px-3 py-2 text-center w-12"></th>
                </tr>
              </thead>
              <tbody>
                {visibleRows.map((v, i) => {
                  const isExp = !!expanded[v.factura_id];
                  return (
                    <>
                      <tr key={v.factura_id}
                          className="border-t border-neutral-100 hover:bg-neutral-50/60 cursor-pointer"
                          onClick={() => toggle(v.factura_id)}
                          data-testid={`lista-incorrecta-row-${i}`}>
                        <td className="px-3 py-1.5 text-neutral-400 text-center">
                          {isExp ? <CaretDown size={11} weight="bold" /> : <CaretRight size={11} weight="bold" />}
                        </td>
                        <td className="px-3 py-1.5 font-mono text-neutral-600">{v.fecha}</td>
                        <td className="px-3 py-1.5 font-mono">
                          <div className="font-bold">{v.numero || "—"}</div>
                          <div className="text-[10px] text-neutral-400">{v.tipo_comprobante || "—"}{v.origen === "pos" ? " · POS" : ""}</div>
                        </td>
                        <td className="px-3 py-1.5">
                          <div className="font-medium truncate max-w-[280px]" title={v.cliente_nombre}>{v.cliente_nombre}</div>
                          <div className="text-[10px] text-neutral-500 font-mono">Lista asignada: <span className="text-neutral-800 font-bold">{v.lista_cliente_nombre}</span></div>
                        </td>
                        <td className="px-3 py-1.5 text-center">
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-sm text-[10px] font-bold uppercase bg-amber-100 text-amber-900 border border-amber-300">
                            <Warning size={9} weight="fill" /> {v.cant_items_error}
                          </span>
                        </td>
                        <td className="px-3 py-1.5 text-right font-mono">{fmtMoney(v.total)}</td>
                        <td className="px-3 py-1.5 text-center" onClick={(e) => e.stopPropagation()}>
                          <Link
                            to={`/facturas?highlight=${v.factura_id}`}
                            className="text-sky-700 hover:text-sky-900 inline-flex"
                            title="Abrir factura"
                            data-testid={`lista-incorrecta-open-${i}`}
                          >
                            <ArrowSquareOut size={14} weight="bold" />
                          </Link>
                        </td>
                      </tr>
                      {isExp && (
                        <tr className="bg-amber-50/40">
                          <td colSpan={7} className="px-4 py-2">
                            <table className="w-full text-[11px]">
                              <thead>
                                <tr className="text-[9px] uppercase font-mono text-neutral-500">
                                  <th className="px-2 py-1 text-left">Producto</th>
                                  <th className="px-2 py-1 text-right">Kg</th>
                                  <th className="px-2 py-1 text-right">Precio facturado c/IVA</th>
                                  <th className="px-2 py-1 text-right">Precio lista cliente</th>
                                  <th className="px-2 py-1 text-right">Δ %</th>
                                  <th className="px-2 py-1 text-left">Diagnóstico</th>
                                </tr>
                              </thead>
                              <tbody>
                                {v.items_con_error.map((it, j) => (
                                  <tr key={j} className="border-t border-amber-100"
                                      data-testid={`lista-incorrecta-item-${i}-${j}`}>
                                    <td className="px-2 py-1">
                                      <div className="font-medium">{it.descripcion}</div>
                                      {it.codigo && <div className="text-[9px] font-mono text-neutral-400">{it.codigo}</div>}
                                    </td>
                                    <td className="px-2 py-1 text-right font-mono">{fmtKg(it.kilos)}</td>
                                    <td className="px-2 py-1 text-right font-mono font-bold">{fmtMoney(it.precio_facturado_con_iva)}</td>
                                    <td className="px-2 py-1 text-right font-mono">
                                      {it.precio_lista_cliente != null ? fmtMoney(it.precio_lista_cliente) : "—"}
                                    </td>
                                    <td className={`px-2 py-1 text-right font-mono font-bold ${
                                      (it.diferencia_pct || 0) < 0 ? "text-rose-700" : "text-amber-700"
                                    }`}>
                                      {it.diferencia_pct != null ? `${it.diferencia_pct > 0 ? "+" : ""}${it.diferencia_pct}%` : "—"}
                                    </td>
                                    <td className="px-2 py-1">
                                      {it.tipo_flag === "lista_ajena" ? (
                                        <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-sm text-[9px] font-bold uppercase bg-orange-100 text-orange-900 border border-orange-300">
                                          Cobrado con lista <span className="font-black">{it.lista_detectada_nombre}</span>
                                        </span>
                                      ) : (
                                        <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-sm text-[9px] font-bold uppercase bg-rose-100 text-rose-900 border border-rose-300">
                                          <Warning size={9} weight="fill" /> Fuera de toda lista
                                        </span>
                                      )}
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </td>
                        </tr>
                      )}
                    </>
                  );
                })}
                {filtered.length === 0 && (
                  <tr>
                    <td colSpan={7} className="px-3 py-8 text-center text-neutral-400 text-[12px]">
                      Sin resultados con el filtro actual.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
          {filtered.length > 10 && (
            <div className="px-5 py-3 border-t border-neutral-200 bg-neutral-50 flex items-center justify-between gap-3 flex-wrap"
                 data-testid="lista-incorrecta-paginacion">
              <div className="text-[11px] font-mono text-neutral-600">
                Mostrando <span className="font-bold text-neutral-800">{visibleRows.length}</span> de{" "}
                <span className="font-bold text-neutral-800">{filtered.length}</span> ventas
              </div>
              <div className="flex items-center gap-2">
                <label className="text-[10px] uppercase font-mono text-neutral-500">Ver</label>
                <select
                  value={visibleCount >= filtered.length ? "all" : String(visibleCount)}
                  onChange={(e) => {
                    const val = e.target.value;
                    setVisibleCount(val === "all" ? filtered.length : parseInt(val, 10));
                  }}
                  className="h-8 text-[12px] font-mono border border-neutral-300 rounded-sm px-2 bg-white focus:outline-none focus:border-[#6B1F24]"
                  data-testid="lista-incorrecta-page-size"
                >
                  <option value="10">10</option>
                  <option value="25">25</option>
                  <option value="50">50</option>
                  <option value="100">100</option>
                  <option value="all">Todos</option>
                </select>
                {visibleCount < filtered.length && (
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => setVisibleCount((v) => Math.min(filtered.length, v + 10))}
                    className="h-8 rounded-sm text-[11px]"
                    data-testid="lista-incorrecta-ver-mas"
                  >
                    Ver 10 más
                  </Button>
                )}
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
